import { Collection } from '@/types/documents/documents.types';
import { DataRoomFile } from '@/types/documents/documents.types';
import {
  FolderViewContainer,
  FolderGrid,
  FolderCard,
  FolderIcon,
  FolderName,
  FolderMeta,
  FolderCount,
  BackButton,
} from './FolderView.styles';
import { DocumentGrid } from '../DocumentGrid/DocumentGrid';
import { EmptyState } from '../EmptyState/EmptyState';
import { Headers } from '@tw/tw-component-library';
import { FiArrowLeft } from 'react-icons/fi';
import { filterDocumentsByCollection } from '@/utils/documents/documentFilters';

interface FolderViewProps {
  smartCollections: Collection[];
  regularCollections: Collection[];
  allCollections: Collection[];
  documentsWithCollection: DataRoomFile[];
  activeCollectionFilter: string | null;
  onCollectionClick: (collectionId: string | null) => void;
  onDocumentClick: (doc: DataRoomFile) => void;
  onDownload: (doc: DataRoomFile) => void;
  onGetSuggestions: (doc: DataRoomFile) => void;
  onMoveToCollection: (doc: DataRoomFile) => void;
  onDelete: (docId: string) => void;
  getFileCollectionInfo: (fileId: string) => {
    name: string;
    isSmart: boolean;
  } | null;
  onView: (doc: DataRoomFile) => void;
  onLinkToAsk?: (doc: DataRoomFile) => void;
  workspaceId?: string;
  isCompact?: boolean;
}

export const FolderView = ({
  smartCollections,
  regularCollections,
  allCollections,
  documentsWithCollection,
  activeCollectionFilter,
  onCollectionClick,
  onDocumentClick,
  onDownload,
  onGetSuggestions,
  onMoveToCollection,
  onDelete,
  getFileCollectionInfo,
  onView,
  onLinkToAsk,
  workspaceId,
  isCompact = false,
}: FolderViewProps) => {
  const getCollectionCount = (collection: Collection) => {
    if (collection.count !== undefined) {
      return collection.count;
    }
    if (collection.files) {
      return Object.keys(collection.files).length;
    }
    // Count documents in this collection
    const collectionId = collection.id || collection.collectionId;
    if (!collectionId) return 0;
    return documentsWithCollection.filter(
      (doc) =>
        doc.collectionId === collectionId ||
        doc.dataRoomCollectionId === collectionId
    ).length;
  };

  const getCollectionDocuments = (collectionId: string | null) => {
    return filterDocumentsByCollection(documentsWithCollection, collectionId);
  };

  // If a collection is selected, show its documents
  if (activeCollectionFilter) {
    const selectedCollection = allCollections.find(
      (col) => (col.id || col.collectionId) === activeCollectionFilter
    );
    const collectionDocuments = getCollectionDocuments(activeCollectionFilter);

    return (
      <FolderViewContainer>
        <BackButton
          $isCompact={isCompact}
          onClick={() => onCollectionClick(null)}
        >
          {isCompact ? <FiArrowLeft /> : '←'} Back to Collections
        </BackButton>
        {selectedCollection && (
          <div style={{ marginBottom: '1.5rem' }}>
            <Headers headerType="h2">
              {selectedCollection.isSmartCollection ? '✨ ' : '📁 '}
              {selectedCollection.name ||
                selectedCollection.title ||
                'Unnamed Collection'}
            </Headers>
            {selectedCollection.description && (
              <div
                style={{
                  fontSize: '0.875rem',
                  color: '#666',
                  marginTop: '0.5rem',
                }}
              >
                {selectedCollection.description}
              </div>
            )}
          </div>
        )}
        {collectionDocuments.length === 0 ? (
          <EmptyState
            icon="📄"
            title="No documents in this collection"
            subtitle="Upload documents to this collection to see them here"
            isCompact={isCompact}
          />
        ) : (
          <DocumentGrid
            documents={collectionDocuments}
            onDocumentClick={onDocumentClick}
            onDownload={onDownload}
            onGetSuggestions={onGetSuggestions}
            onMoveToCollection={onMoveToCollection}
            onDelete={(doc) => onDelete(doc.id || '')}
            getFileCollectionInfo={getFileCollectionInfo}
            onView={onView}
            onLinkToAsk={onLinkToAsk}
            workspaceId={workspaceId}
            isCompact={isCompact}
          />
        )}
      </FolderViewContainer>
    );
  }

  // Show all collections as folders
  return (
    <FolderViewContainer>
      {smartCollections.length > 0 && (
        <>
          <Headers headerType="caption" styles={{ marginBottom: '1rem' }}>
            🤖 AI-SUGGESTED COLLECTIONS
          </Headers>
          <FolderGrid $isCompact={isCompact}>
            {smartCollections.map((collection: Collection) => {
              const collectionId =
                collection.id || collection.collectionId || null;
              const count = getCollectionCount(collection);

              return (
                <FolderCard
                  key={collectionId}
                  $isCompact={isCompact}
                  onClick={() => onCollectionClick(collectionId)}
                >
                  <FolderIcon $isCompact={isCompact}>✨</FolderIcon>
                  <FolderName $isCompact={isCompact}>
                    {collection.name ||
                      collection.title ||
                      'Unnamed Collection'}
                  </FolderName>
                  <FolderMeta>
                    <FolderCount $isCompact={isCompact}>
                      {count} document{count !== 1 ? 's' : ''}
                    </FolderCount>
                    {collection.description && (
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: '#999',
                          marginTop: '0.25rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {collection.description}
                      </div>
                    )}
                  </FolderMeta>
                </FolderCard>
              );
            })}
          </FolderGrid>
        </>
      )}

      {regularCollections.length > 0 && (
        <>
          <Headers
            headerType="caption"
            styles={{
              marginTop: smartCollections.length > 0 ? '2rem' : '0',
              marginBottom: '1rem',
            }}
          >
            📁 WORKSPACE COLLECTIONS
          </Headers>
          <FolderGrid $isCompact={isCompact}>
            {regularCollections.map((collection: Collection) => {
              const collectionId =
                collection.id || collection.collectionId || null;
              const count = getCollectionCount(collection);

              return (
                <FolderCard
                  key={collectionId}
                  $isCompact={isCompact}
                  onClick={() => onCollectionClick(collectionId)}
                >
                  <FolderIcon $isCompact={isCompact}>📁</FolderIcon>
                  <FolderName $isCompact={isCompact}>
                    {collection.name ||
                      collection.title ||
                      'Unnamed Collection'}
                  </FolderName>
                  <FolderMeta>
                    <FolderCount $isCompact={isCompact}>
                      {count} document{count !== 1 ? 's' : ''}
                    </FolderCount>
                    {collection.description && (
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: '#999',
                          marginTop: '0.25rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {collection.description}
                      </div>
                    )}
                  </FolderMeta>
                </FolderCard>
              );
            })}
          </FolderGrid>
        </>
      )}

      {smartCollections.length === 0 && regularCollections.length === 0 && (
        <EmptyState
          icon="📁"
          title="No collections yet"
          subtitle="Create a collection to organize your documents"
          isCompact={isCompact}
        />
      )}
    </FolderViewContainer>
  );
};
